/**
 * Config pública do widget Chatwoot (Website channel).
 * Aliases: NEXT_PUBLIC_CHATWOOT_URL / NEXT_PUBLIC_CHATWOOT_TOKEN.
 */

import { readEnvFlag } from "@/lib/env/flag"

export type ChatwootPublicConfig = {
  enabled: boolean
  baseUrl: string
  websiteToken: string
}

export function resolveChatwootPublicConfig(): ChatwootPublicConfig {
  const flagOn = readEnvFlag("NEXT_PUBLIC_CHATWOOT_ENABLED")
  const baseUrl = (
    process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL ||
    process.env.NEXT_PUBLIC_CHATWOOT_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "")
  const websiteToken = (
    process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN ||
    process.env.NEXT_PUBLIC_CHATWOOT_TOKEN ||
    ""
  ).trim()

  return {
    enabled: flagOn && Boolean(baseUrl) && Boolean(websiteToken),
    baseUrl,
    websiteToken,
  }
}

/** Widget público (SDK Website channel). */
export function isChatwootPublicWidgetEnabled(): boolean {
  return resolveChatwootPublicConfig().enabled
}

export type ChatwootPublicWidgetUser = {
  identifier: string
  name?: string | null
  email?: string | null
}

/**
 * Em dev: avisa se ENABLED=true mas falta baseUrl/token (caso comum de “ativei a flag e nada aparece”).
 * Não loga valores — só presença.
 */
export function warnIfChatwootPublicConfigIncomplete(): void {
  if (process.env.NODE_ENV === "production") return
  if (!readEnvFlag("NEXT_PUBLIC_CHATWOOT_ENABLED")) return
  const { baseUrl, websiteToken, enabled } = resolveChatwootPublicConfig()
  if (enabled) return
  const missing: string[] = []
  if (!baseUrl) missing.push("NEXT_PUBLIC_CHATWOOT_BASE_URL (ou NEXT_PUBLIC_CHATWOOT_URL)")
  if (!websiteToken) missing.push("NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN (ou NEXT_PUBLIC_CHATWOOT_TOKEN)")
  console.warn(
    `[chatwoot] NEXT_PUBLIC_CHATWOOT_ENABLED=true mas config incompleta (faltando: ${missing.join(", ")}). ` +
      "Reinicie o `next dev` após alterar .env.local. O balão fica oculto em /admin e /render.",
  )
}

function applyChatwootUser(user?: ChatwootPublicWidgetUser | null): void {
  if (!user?.identifier || !window.$chatwoot?.setUser) return
  const attrs: { name?: string; email?: string } = {}
  if (user.name) attrs.name = user.name
  if (user.email) attrs.email = user.email
  window.$chatwoot.setUser(user.identifier, attrs)
}

/**
 * Abre o painel do Chatwoot; aguarda `chatwoot:ready` se o SDK ainda não carregou.
 * `user` opcional: identifica o visitante via `$chatwoot.setUser` (ex.: `/painel/suporte`).
 */
export function openChatwootPublicWidget(user?: ChatwootPublicWidgetUser | null): void {
  if (typeof window === "undefined") return

  const open = () => {
    applyChatwootUser(user)
    window.$chatwoot?.toggleBubbleVisibility?.("show")
    window.$chatwoot?.toggle?.("open")
  }

  if (window.$chatwoot) {
    open()
    return
  }

  const onReady = () => {
    window.removeEventListener("chatwoot:ready", onReady)
    window.clearTimeout(timeoutId)
    open()
  }
  const timeoutId = window.setTimeout(() => {
    window.removeEventListener("chatwoot:ready", onReady)
  }, 15_000)
  window.addEventListener("chatwoot:ready", onReady)
}
